/* eslint-disable @typescript-eslint/explicit-function-return-type */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BeatLoader } from 'react-spinners';
import { format } from 'date-fns';

import {
  getSellerIdByContentManagerUserId,
  getStudentActivityLogs,
  getStudents,
} from '../../../services/api';
import { getLmsKey as getStoredLmsKey } from '../../../services/auth';
import { useAuth } from '../../../hooks/auth';
import { useToast } from '../../../hooks/toast';
import {
  AlertWrapper,
  Container,
  FilterGrid,
  FilterGroup,
  Filters,
  KpiCard,
  KpiGrid,
  TopList,
} from './styles';

interface IStudent {
  id: string;
  name: string;
  email: string;
  course: string;
}

interface IActivityLog {
  id?: string;
  student_id: string;
  student_name?: string;
  student_course?: string;
  action: string;
  item_name?: string;
  created_at: string;
}

interface IActivityResponse {
  data: IActivityLog[];
  total: number;
  startRow: number;
  endRow: number;
}

const ALL = '__ALL__';
const ACTION_LABELS: Record<string, string> = {
  'viewed topic': 'Topico visualizado',
  'viewed item': 'Atividade visualizada',
  'viewed content': 'Conteudo visualizado',
  'started activity': 'Atividade iniciada',
  'completed activity': 'Atividade concluida',
  'finished activity': 'Atividade finalizada',
  'answered question': 'Questao respondida',
  'submitted answer': 'Resposta enviada',
  login: 'Login',
  logout: 'Logout',
  'opened material': 'Material aberto',
  'downloaded material': 'Material baixado',
};

const normalizeAction = (value?: string): string =>
  (value || '')
    .toLowerCase()
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');

const getActionLabel = (value?: string): string => {
  const normalized = normalizeAction(value);
  return ACTION_LABELS[normalized] || value || 'Sem acao';
};

const LogsReport: React.FC = () => {
  const { user } = useAuth();
  const { addToast } = useToast();

  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState<IStudent[]>([]);
  const [logs, setLogs] = useState<IActivityLog[]>([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedAction, setSelectedAction] = useState(ALL);
  const [selectedStudent, setSelectedStudent] = useState(ALL);
  const [selectedCourse, setSelectedCourse] = useState(ALL);
  const [itemName, setItemName] = useState('');
  const requestInFlightRef = useRef(false);

  useEffect(() => {
    const firstDayOfMonth = format(
      new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      'yyyy-MM-dd',
    );
    const today = format(new Date(), 'yyyy-MM-dd');
    setStartDate(firstDayOfMonth);
    setEndDate(today);
  }, []);

  const resolveLmsKey = useCallback(async (): Promise<string | null> => {
    let lmsKey = getStoredLmsKey();

    if (!lmsKey && user?.id) {
      try {
        const { data } = await getSellerIdByContentManagerUserId(user.id);
        lmsKey = data.lms_key || null;
      } catch {
        lmsKey = null;
      }
    }

    return lmsKey;
  }, [user?.id]);

  const loadData = useCallback(async () => {
    if (!startDate || !endDate) return;
    if (requestInFlightRef.current) return;

    try {
      requestInFlightRef.current = true;
      setLoading(true);

      const lmsKey = await resolveLmsKey();
      if (!lmsKey) {
        addToast({
          title: 'Chave LMS não encontrada',
          type: 'error',
          description: 'Não foi possível identificar a chave LMS desta conta.',
        });
        setLogs([]);
        setStudents([]);
        return;
      }

      const { data: studentsData } = await getStudents(lmsKey);

      const params: any = {
        start_date: startDate,
        end_date: endDate,
      };

      if (selectedAction !== ALL) {
        params.action = selectedAction;
      }

      if (selectedStudent !== ALL) {
        params.student_ids = [selectedStudent];
      }

      if (itemName.trim()) {
        params.item_name = itemName.trim();
      }

      const { data: logsData } = await getStudentActivityLogs(lmsKey, params);

      const responseData = logsData as IActivityResponse | IActivityLog[];
      const activityLogs = Array.isArray(responseData)
        ? responseData
        : responseData.data || [];

      setStudents(Array.isArray(studentsData) ? studentsData : []);
      setLogs(activityLogs);
    } catch (error) {
      setLogs([]);
      addToast({
        type: 'error',
        title: 'Erro ao carregar relatório',
        description: 'Não foi possível buscar os dados de atividades dos alunos.',
      });
    } finally {
      requestInFlightRef.current = false;
      setLoading(false);
    }
  }, [
    addToast,
    endDate,
    itemName,
    resolveLmsKey,
    selectedAction,
    selectedStudent,
    startDate,
  ]);

  const studentByIdentifier = useMemo(() => {
    const map = new Map<string, IStudent>();
    students.forEach(student => {
      map.set(student.email.toLowerCase(), student);
    });
    return map;
  }, [students]);

  const logsWithCourse = useMemo(() => {
    return logs.map(log => {
      const student = studentByIdentifier.get((log.student_id || '').toLowerCase());
      const studentName = log.student_name || student?.name || log.student_id;
      const studentCourse = log.student_course || student?.course || 'Sem turma';

      return {
        ...log,
        studentName,
        studentCourse,
      };
    });
  }, [logs, studentByIdentifier]);

  const filteredLogs = useMemo(() => {
    if (selectedCourse === ALL) return logsWithCourse;
    return logsWithCourse.filter(log => log.studentCourse === selectedCourse);
  }, [logsWithCourse, selectedCourse]);

  const actionOptions = useMemo(() => {
    return Array.from(
      new Set(logsWithCourse.map(log => log.action).filter(Boolean)),
    )
      .map(action => ({
        value: action,
        label: getActionLabel(action),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [logsWithCourse]);

  const studentOptions = useMemo(() => {
    return students
      .map(student => ({
        label: `${student.name} (${student.email})`,
        value: student.email,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [students]);

  const courseOptions = useMemo(() => {
    return Array.from(
      new Set(
        logsWithCourse.map(log => log.studentCourse).filter(Boolean),
      ),
    ).sort();
  }, [logsWithCourse]);

  const kpis = useMemo(() => {
    const studentsCount = new Set(filteredLogs.map(log => log.student_id)).size;
    const coursesCount = new Set(filteredLogs.map(log => log.studentCourse)).size;

    const actionsMap = filteredLogs.reduce((acc, current) => {
      const key = current.action || 'Sem ação';
      acc.set(key, (acc.get(key) || 0) + 1);
      return acc;
    }, new Map<string, number>());

    const topAction = Array.from(actionsMap.entries()).sort(
      (a, b) => b[1] - a[1],
    )[0];

    const courseMap = filteredLogs.reduce((acc, current) => {
      const key = current.studentCourse || 'Sem turma';
      acc.set(key, (acc.get(key) || 0) + 1);
      return acc;
    }, new Map<string, number>());

    const topCourses = Array.from(courseMap.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);

    return {
      totalActivities: filteredLogs.length,
      studentsCount,
      coursesCount,
      topAction: topAction ? `${getActionLabel(topAction[0])} (${topAction[1]})` : '—',
      topCourses,
    };
  }, [filteredLogs]);

  return (
    <Container>
      <header>
        <h1>Dashboard de Atividades dos Alunos</h1>
        <span>{user.name}</span>
      </header>

      <Filters>
        <FilterGrid>
          <FilterGroup>
            <label htmlFor="start_date">Data inicial*</label>
            <input
              id="start_date"
              type="date"
              value={startDate}
              onChange={event => setStartDate(event.target.value)}
            />
          </FilterGroup>

          <FilterGroup>
            <label htmlFor="end_date">Data final*</label>
            <input
              id="end_date"
              type="date"
              value={endDate}
              onChange={event => setEndDate(event.target.value)}
            />
          </FilterGroup>

          <FilterGroup>
            <label htmlFor="action">Ação</label>
            <select
              id="action"
              value={selectedAction}
              onChange={event => setSelectedAction(event.target.value)}
            >
              <option value={ALL}>Todas</option>
              {actionOptions.map(action => (
                <option key={action.value} value={action.value}>
                  {action.label}
                </option>
              ))}
            </select>
          </FilterGroup>

          <FilterGroup>
            <label htmlFor="student">Aluno</label>
            <select
              id="student"
              value={selectedStudent}
              onChange={event => setSelectedStudent(event.target.value)}
            >
              <option value={ALL}>Todos</option>
              {studentOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </FilterGroup>

          <FilterGroup>
            <label htmlFor="course">Turma (curso)</label>
            <select
              id="course"
              value={selectedCourse}
              onChange={event => setSelectedCourse(event.target.value)}
            >
              <option value={ALL}>Todas</option>
              {courseOptions.map(course => (
                <option key={course} value={course}>
                  {course}
                </option>
              ))}
            </select>
          </FilterGroup>

          <FilterGroup>
            <label htmlFor="item_name">Atividade</label>
            <input
              id="item_name"
              type="text"
              placeholder="Nome da atividade"
              value={itemName}
              onChange={event => setItemName(event.target.value)}
            />
          </FilterGroup>
        </FilterGrid>

        <button type="button" onClick={loadData} disabled={loading}>
          {loading ? (
            <>
              <BeatLoader size={7} color="#fff" margin={1} />
              <span style={{ marginLeft: 8 }}>Buscando...</span>
            </>
          ) : (
            'Buscar'
          )}
        </button>
      </Filters>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '30px' }}>
          <BeatLoader size={16} />
        </div>
      ) : (
        <>
          <KpiGrid>
            <KpiCard>
              <small>Total de atividades</small>
              <strong>{kpis.totalActivities}</strong>
            </KpiCard>

            <KpiCard>
              <small>Alunos ativos no período</small>
              <strong>{kpis.studentsCount}</strong>
            </KpiCard>

            <KpiCard>
              <small>Turmas ativas</small>
              <strong>{kpis.coursesCount}</strong>
            </KpiCard>

            <KpiCard>
              <small>Ação mais frequente</small>
              <strong>{kpis.topAction}</strong>
            </KpiCard>
          </KpiGrid>

          <TopList>
            <h3>Top turmas por atividade</h3>
            {kpis.topCourses.length === 0 ? (
              <p>Sem dados para o período/filtros selecionados.</p>
            ) : (
              <ul>
                {kpis.topCourses.map(([course, count]) => (
                  <li key={course}>
                    <span>{course}</span>
                    <strong>{count}</strong>
                  </li>
                ))}
              </ul>
            )}
          </TopList>

          <div className="content-tabs slideUp">
            <table>
              <thead>
                <tr>
                  <th>Data do evento</th>
                  <th>Aluno</th>
                  <th>Turma</th>
                  <th>Ação</th>
                  <th>Atividade</th>
                </tr>
              </thead>
              <tbody>
                {filteredLogs.map(log => (
                  <tr key={`${log.id || log.created_at}-${log.student_id}`}>
                    <td>{format(new Date(log.created_at), 'dd/MM/yyyy HH:mm')}</td>
                    <td>{log.studentName}</td>
                    <td>{log.studentCourse}</td>
                    <td>{getActionLabel(log.action)}</td>
                    <td>{log.item_name || 'Não informada'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!loading && filteredLogs.length === 0 && (
        <AlertWrapper>Sem dados para apresentação</AlertWrapper>
      )}
    </Container>
  );
};

export { LogsReport };
